import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/auth/Login';
import ImpersonateCallback from './components/auth/ImpersonateCallback';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Layout from './components/layout/Layout';
import ErrorBoundary from './components/common/ErrorBoundary';
import Loading from './components/common/Loading';
import SessionWarningModal from './components/common/SessionWarningModal';
import CambiarPasswordModal from './components/common/CambiarPasswordModal';
import useSessionTimeout from './hooks/useSessionTimeout';
import { getDefaultPath } from './config/profilesConfig';
import { removeToken } from './services/api';
import './App.css';

// =============================================================================
// Lazy-loaded views (code splitting por rol)
// =============================================================================

// ROOT SISTEMA
const DashboardRoot = lazy(() => import('./components/root/DashboardRoot'));
const ConfigColegio = lazy(() => import('./components/root/ConfigColegio'));
const Perfiles = lazy(() => import('./components/root/Perfiles'));
const UsuariosSistema = lazy(() => import('./components/root/UsuariosSistema'));

// ROOT UNIVERSIDAD
const UsuariosColegio = lazy(() => import('./components/root/UsuariosColegio'));
const PerfilesColegio = lazy(() => import('./components/root/PerfilesColegio'));
const ConfiguracionColegio = lazy(() => import('./components/root/ConfiguracionColegio'));
const DashboardRootColegio = lazy(() => import('./components/root/DashboardRootColegio'));

// ADMIN
const DashboardAdmin = lazy(() => import('./components/admin/DashboardAdmin'));
const CohortesAdmin = lazy(() => import('./components/admin/CohortesAdmin'));
const ProgramasAdmin = lazy(() => import('./components/admin/ProgramasAdmin'));
const UnidadesAprendizajeAdmin = lazy(() => import('./components/admin/UnidadesAprendizajeAdmin'));
const MatriculasAdmin = lazy(() => import('./components/admin/MatriculasAdmin'));
const CargaFormacionAdmin = lazy(() => import('./components/admin/CargaFormacionAdmin'));
const ContenidoProgramaticoAdmin = lazy(() => import('./components/admin/ContenidoProgramaticoAdmin'));
const ContenidosAdmin = lazy(() => import('./components/admin/ContenidosAdmin'));
const EscalasNotasAdmin = lazy(() => import('./components/admin/EscalasNotasAdmin'));
const ConfigNotasAdmin = lazy(() => import('./components/admin/ConfigNotasAdmin'));
const InasistenciasReporteAdmin = lazy(() => import('./components/admin/InasistenciasReporteAdmin'));

// DOCENTE
const DashboardDocente = lazy(() => import('./components/docente/DashboardDocente'));
const NotasDocente = lazy(() => import('./components/docente/NotasDocente'));
const InasistenciasDocente = lazy(() => import('./components/docente/InasistenciasDocente'));
const ContenidoProgramaticoDocente = lazy(() => import('./components/docente/ContenidoProgramaticoDocente'));
const SabanaNotasDocente = lazy(() => import('./components/docente/SabanaNotasDocente'));
const LibroFinalDocente = lazy(() => import('./components/docente/LibroFinalDocente'));

// ESTUDIANTE
const DashboardEstudiante = lazy(() => import('./components/estudiante/DashboardEstudiante'));
const NotasEstudiante = lazy(() => import('./components/estudiante/NotasEstudiante'));
const NotasFormacionEstudiante = lazy(() => import('./components/estudiante/NotasFormacionEstudiante'));

// Configuracion de sesion
const SESSION_CONFIG = {
  timeoutMinutes: 30,
  warningSeconds: 60,
  showWarning: true
};

function App() {
  const [isLogged, setIsLogged] = useState(false);
  const [user, setUser] = useState(null);
  const [showCambiarPassword, setShowCambiarPassword] = useState(false);

  // Verificar si hay sesion guardada al cargar la app
  // Primero buscar en sessionStorage (sesiones de impersonation, específicas de pestaña)
  // Luego en localStorage (sesiones normales, compartidas entre pestañas)
  useEffect(() => {
    // Priorizar sessionStorage para sesiones de impersonation
    const sessionUser = sessionStorage.getItem('user');
    const localUser = localStorage.getItem('user');
    const savedUser = sessionUser || localUser;

    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      setIsLogged(true);
      // Si debe cambiar contraseña, mostrar modal
      if (parsedUser.debeCambiarPassword) {
        setShowCambiarPassword(true);
      }
    }
  }, []);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setIsLogged(true);
    // Si debe cambiar contraseña, mostrar modal
    if (userData.debeCambiarPassword) {
      setShowCambiarPassword(true);
    }
  };

  const handlePasswordChanged = () => {
    // Actualizar el estado del usuario
    setUser(prev => ({ ...prev, debeCambiarPassword: false }));
    setShowCambiarPassword(false);
  };

  const handleLogout = () => {
    // Limpiar ambos storages
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('isImpersonated');
    localStorage.removeItem('user');
    removeToken(); // Eliminar token JWT de ambos storages
    setUser(null);
    setIsLogged(false);
    window.location.replace('/');
  };

  // Hook de timeout de sesion
  const {
    showWarningModal,
    remainingSeconds,
    extendSession,
    confirmLogout
  } = useSessionTimeout({
    timeoutMinutes: SESSION_CONFIG.timeoutMinutes,
    warningSeconds: SESSION_CONFIG.warningSeconds,
    showWarning: SESSION_CONFIG.showWarning,
    onLogout: handleLogout,
    isActive: isLogged
  });

  if (!isLogged) {
    return (
      <BrowserRouter>
        <Routes>
          {/* Ruta especial para impersonation (acceso desde Root Sistema) */}
          <Route path="/impersonate" element={<ImpersonateCallback onLoginSuccess={handleLoginSuccess} />} />
          <Route path="*" element={<Login onLoginSuccess={handleLoginSuccess} />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Obtener ruta por defecto segun el perfil
  const defaultPath = getDefaultPath(user.role, user.tipoUsuario);

  return (
    <BrowserRouter>
      <ErrorBoundary>
      <Layout user={user} onLogout={handleLogout} onCambiarPassword={() => setShowCambiarPassword(true)}>
        <Suspense fallback={<Loading />}>
        <Routes>
          {/* Redirigir raiz a la ruta por defecto */}
          <Route path="/" element={<Navigate to={defaultPath} replace />} />

          {/* ===== RUTAS ROOT SISTEMA ===== */}
          {user.role === 'Root' && user.tipoUsuario === 'SISTEMA' && [
            <Route key="rs-dashboard" path="/dashboard" element={<DashboardRoot />} />,
            <Route key="rs-colegios" path="/colegios" element={<ConfigColegio />} />
          ]}

          {/* ===== RUTAS ROOT SISTEMA + ROOT UNIVERSIDAD (compartidas) ===== */}
          {user.role === 'Root' && [
            <Route key="perfiles" path="/perfiles" element={
              user.tipoUsuario === 'SISTEMA' ? <Perfiles /> : <PerfilesColegio />
            } />,
            <Route key="usuarios" path="/usuarios" element={
              user.tipoUsuario === 'SISTEMA' ? <UsuariosSistema /> : <UsuariosColegio />
            } />
          ]}

          {/* ===== RUTAS ROOT UNIVERSIDAD ===== */}
          {user.role === 'Root' && user.tipoUsuario === 'COLEGIO' && [
            <Route key="rc-dashboard" path="/dashboard" element={<DashboardRootColegio user={user} />} />,
            <Route key="rc-configuracion" path="/configuracion" element={<ConfiguracionColegio />} />
          ]}

          {/* ===== RUTAS ADMIN ===== */}
          {user.role === 'Admin' && [
            <Route key="dashboard" path="/dashboard" element={<DashboardAdmin user={user} />} />,
            <Route key="cohortes" path="/configuracion-academica/cohortes" element={<CohortesAdmin />} />,
            <Route key="programas" path="/configuracion-academica/programas" element={<ProgramasAdmin />} />,
            <Route key="unidades-aprendizaje" path="/configuracion-academica/unidades-aprendizaje" element={<UnidadesAprendizajeAdmin />} />,
            <Route key="carga-academica" path="/configuracion-academica/carga-academica" element={<CargaFormacionAdmin />} />,
            <Route key="contenido-programatico" path="/configuracion-academica/contenido-programatico" element={<ContenidoProgramaticoAdmin />} />,
            <Route key="matriculas" path="/configuracion-academica/matriculas" element={<MatriculasAdmin />} />,
            <Route key="contenidos" path="/configuracion-academica/contenidos" element={<ContenidosAdmin />} />,
            <Route key="escalas-notas" path="/configuracion-academica/escalas-notas" element={<EscalasNotasAdmin />} />,
            <Route key="config-notas" path="/configuracion-academica/config-notas" element={<ConfigNotasAdmin />} />,
            <Route key="inasistencias-reporte" path="/inasistencias-reporte" element={<InasistenciasReporteAdmin user={user} />} />,
            <Route key="sabana-notas" path="/reportes/sabana" element={<SabanaNotasDocente user={user} />} />,
            <Route key="libro-final" path="/reportes/libro-final" element={<LibroFinalDocente user={user} />} />,
            <Route key="usuarios" path="/usuarios" element={<UsuariosColegio />} />
          ]}

          {/* ===== RUTAS DOCENTE ===== */}
          {user.role === 'Docente' && [
            <Route key="dashboard" path="/dashboard" element={<DashboardDocente user={user} />} />,
            <Route key="notas" path="/notas" element={<NotasDocente user={user} />} />,
            <Route key="inasistencias" path="/inasistencias" element={<InasistenciasDocente user={user} />} />,
            <Route key="contenido-programatico" path="/contenido-programatico" element={<ContenidoProgramaticoDocente user={user} />} />,
            <Route key="sabana-notas" path="/reportes/sabana" element={<SabanaNotasDocente user={user} />} />,
            <Route key="libro-final" path="/reportes/libro-final" element={<LibroFinalDocente user={user} />} />
          ]}

          {/* ===== RUTAS ESTUDIANTE ===== */}
          {user.role === 'Estudiante' && [
            <Route key="dashboard" path="/dashboard" element={<DashboardEstudiante />} />,
            <Route key="notas" path="/notas" element={<NotasFormacionEstudiante />} />
          ]}

          {/* Ruta por defecto - redirigir */}
          <Route path="*" element={<Navigate to={defaultPath} replace />} />
        </Routes>
        </Suspense>
      </Layout>

      {showWarningModal && (
        <SessionWarningModal
          remainingSeconds={remainingSeconds}
          onExtend={extendSession}
          onLogout={confirmLogout}
        />
      )}

      {showCambiarPassword && (
        <CambiarPasswordModal
          isOpen={showCambiarPassword}
          onClose={() => setShowCambiarPassword(false)}
          onSuccess={handlePasswordChanged}
          obligatorio={user?.debeCambiarPassword}
        />
      )}
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
